// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { sendToServer, utils } from '@seedplanner/shared-utils';
import * as React from 'react';
import { PageError } from '../components/common/error';
import * as routes from '../routes';

interface Props {
  readonly children: React.ReactNode;
}

interface State {
  readonly hasError: boolean;
  readonly loadTime: number;
  readonly error?: Error;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: undefined, loadTime: Date.now() };
  }

  static getDerivedStateFromError(e: unknown) {
    const error = utils.asErrorInstance(e);
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(e: unknown, info: React.ErrorInfo) {
    const error = utils.asErrorInstance(e);
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    // Timestamp is a hack to know which sourcemaps to use when debugging in prod
    const currentUrl = window?.location?.toString() ?? 'window.location is undefined';
    void sendErrorToServer(error.message, info.componentStack, this.state.loadTime, currentUrl);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <PageError error={this.state.error} />;
    }

    return this.props.children;
  }
}

const sendErrorToServer = (errorMessage: string, stack: string, loadTime: number, location: string) => {
  sendToServer({
    route: routes.ERROR,
    body: JSON.stringify({ errorMessage, stack, loadTime, location }),
    title: 'error_boundary_report',
  });
};
